import React, { useState } from "react";
import {
    PrimaryButton,
    DefaultButton,
    Stack,
    TextField,
    Spinner,
    ChoiceGroup,
    IChoiceGroupOption,
    Checkbox,
} from "@fluentui/react";
import { queryIcons, getIconUrl } from "src/services/aigc/workflowOnline";
import { IconResultItem } from "src/types/icon";
import style from "./index.module.scss";

export const Icon: React.FC = () => {
    const [query, setQuery] = useState("");
    const [selectedStyles, setSelectedStyles] = useState<string[]>(["brands", "solid"]);
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<IconResultItem[]>([]);
    const [llmChosenIndex, setLlmChosenIndex] = useState<number | null>(null);
    const [llmChooseReason, setLlmChooseReason] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pageIndex, setPageIndex] = useState<number>(1);
    const pageSize = 15;
    const [totalCount, setTotalCount] = useState<number>(0);
    const [lastSearchTimeMs, setLastSearchTimeMs] = useState<number | null>(null);

    // State to store raw SVG markup for each icon id
    const [iconSvgs, setIconSvgs] = React.useState<Record<string, string>>({});

    // In-memory cache of icon urls keyed by icon id to avoid refetching the same icon.
    // We keep this in a ref so it survives re-renders without causing updates.
    // Cache entries include an expiresAt timestamp to support TTL-based invalidation.
    const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day
    const iconUrlCacheRef = React.useRef<Record<string, { url: string; expiresAt: number }>>({});

    const iconStyles: string[] = ["brands", "regular", "solid"];

    const searchModeChoices: IChoiceGroupOption[] = [
        { key: "text", text: "Text Search" },
        { key: "vector", text: "Vector Search" },
        { key: "hybrid", text: "Hybrid Search" },
    ];

    // Search mode (Text, Vector, Hybrid) & Semantic Ranking toggle
    const [searchMode, setSearchMode] = useState<"text" | "vector" | "hybrid">("hybrid");
    const [semanticRanking, setSemanticRanking] = useState<boolean>(false);
    const [rankWithLLM, setRankWithLLM] = useState<boolean>(true);
    const [searchModeStatus, setSearchModeStatus] = useState<null | "text" | "vector" | "hybrid">(null);
    // const [semanticRankingStatus, setSemanticRankingStatus] = useState<null | boolean>(null);

    const runSearch = async (page = 1) => {
        const start = Date.now();
        setError(null);
        setIsLoading(true);
        // If starting a fresh search (page 1) clear results and SVG cache
        if (page === 1) {
            setResults([]);
            setIconSvgs({});
            setSearchModeStatus(null);
            // setSemanticRankingStatus(null);
        }

        try {
            const resp = await queryIcons({
                query,
                styles: selectedStyles,
                pageIndex: page,
                pageSize,
                options: {
                    textSearch: searchMode === "text" || searchMode === "hybrid",
                    vectorSearch: searchMode === "vector" || searchMode === "hybrid",
                    semanticRanking,
                },
                rankWithLLM: rankWithLLM && page === 1, // only rank first page with LLM
            });
            const searchResult = resp?.searchResult ?? resp?.SearchResult ?? [];
            const items = searchResult?.items ?? searchResult?.Items ?? [];
            const total = searchResult?.totalCount ?? searchResult?.TotalCount ?? items.length;
            setTotalCount(Number(total));
            setSearchModeStatus(searchMode);
            // setSemanticRankingStatus(semanticRanking);

            setLlmChosenIndex(resp.rankResponse?.index);
            setLlmChooseReason(resp.rankResponse?.reason);

            // Append or set items depending on whether this is a continuation
            if (page === 1) {
                setResults(items as IconResultItem[]);
            } else {
                setResults((prev) => ([...(prev || []), ...(items as IconResultItem[])]));
            }

            // For each returned item, fetch its SVG via service (normalize id lookup)
            (items as any[]).forEach(async (item: any) => {
                const id = item?.document?.id;
                if (!id) return;

                // If we already have a cached URL for this id and it's not expired, use it and skip fetching
                const cachedEntry = iconUrlCacheRef.current[id];
                if (cachedEntry) {
                    if (cachedEntry.expiresAt > Date.now()) {
                        setIconSvgs((prev) => ({ ...prev, [id]: cachedEntry.url }));
                        return;
                    }

                    // expired - remove it so we fetch a fresh URL
                    delete iconUrlCacheRef.current[id];
                }

                try {
                    const url = await getIconUrl({ iconId: id });

                    // Store in the in-memory cache with an expiration so future searches/pages reuse it
                    iconUrlCacheRef.current[id] = { url, expiresAt: Date.now() + CACHE_TTL_MS };

                    setIconSvgs((prev) => ({ ...prev, [id]: url }));
                } catch (err: any) {
                    const responseData = err?.response?.data;
                    const errText = responseData
                        ? (typeof responseData === "string" ? responseData : JSON.stringify(responseData))
                        : (err?.message ?? String(err));
                    const message = `Error fetching icon ${String(id)} svg: ${errText}`;
                    console.error(message, err);
                    setError(message);
                }
            });
        } catch (err: any) {
            setError(String(err?.message ?? err));
        } finally {
            setIsLoading(false);
            try {
                const duration = Date.now() - start;
                setLastSearchTimeMs(duration);
            } catch (e) {
                // ignore timing errors
            }
        }
    };

    const calculateColor = (score: number | undefined, minScore: number, maxScore: number) => {
        if (typeof score !== "number") return "black";

        const s = Math.max(minScore, Math.min(maxScore, score));
        const red = 255 * ((maxScore - s) / (maxScore - minScore));
        const green = 255 * ((s - minScore) / (maxScore - minScore));

        return `rgb(${red}, ${green}, 0)`;
    };

    React.useEffect(() => () => setIconSvgs({}), []);

    return (
        <div className={style.iconsPage}>
            <h1>Icon Search</h1>
            <Stack tokens={{ childrenGap: 12 }} className={style.container}>
                <div className={style.searchBar}>
                    <Stack horizontal tokens={{ childrenGap: 12 }}>
                        <TextField
                            placeholder="Enter a textual query to search similar icons"
                            value={query}
                            onChange={(_, v) => setQuery(v || "")}
                            onKeyDown={(e) => { if (e.key === "Enter") { runSearch(1); } }}
                            styles={{ root: { minWidth: 360 } }}
                        />
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            {iconStyles.map((key) => (
                                <Checkbox
                                    key={key}
                                    label={`${key.charAt(0).toUpperCase()}${key.slice(1)}`}
                                    checked={selectedStyles.includes(key)}
                                    onChange={(_, checked) => {
                                        setSelectedStyles((prev) => {
                                            const has = prev.includes(key);
                                            if (checked && !has) return [...prev, key];
                                            if (!checked && has) return prev.filter((s) => s !== key);
                                            return prev;
                                        });
                                    }}
                                />
                            ))}
                        </Stack>
                        <PrimaryButton text="Search" onClick={() => runSearch(1)} />
                        <DefaultButton
                            text="Clear"
                            onClick={() => {
                                setResults([]);
                                setQuery("");
                                setSelectedStyles(["solid"]);
                                setPageIndex(1);
                                setTotalCount(0);
                            }}
                        />
                    </Stack>
                </div>
                <div className={style.searchOptions}>
                    <Stack horizontal tokens={{ childrenGap: 24 }} verticalAlign="center">
                        <ChoiceGroup
                            selectedKey={searchMode}
                            options={searchModeChoices}
                            onChange={(_, opt) => opt && setSearchMode(opt.key as "text" | "vector" | "hybrid")}
                            className={style.searchModeChoiceGroup}
                            styles={{
                                flexContainer: { display: "flex", gap: 12 },
                            }}
                        />
                        <Checkbox
                            label="Semantic Ranking"
                            checked={semanticRanking}
                            onChange={(_, checked) => setSemanticRanking(!!checked)}
                        />
                        <Checkbox
                            label="Rerank with GPT-4o"
                            checked={rankWithLLM}
                            onChange={(_, checked) => setRankWithLLM(!!checked)}
                        />
                    </Stack>
                </div>
                {error && <div className={style.error}>{error}</div>}
                <div className={style.resultsList}>
                    {results.map((r, i) => {
                        const rerankerScore = r?.semanticSearch?.rerankerScore;
                        const score = r?.score;
                        const llmChosen = llmChosenIndex === i;
                        return (
                            <div key={r.document.id} className={llmChosen ? style.cardChosen : style.card}>
                                <div className={style.preview}>
                                    {iconSvgs[r.document.id] ? (
                                        <img src={iconSvgs[r.document.id]} className={style.svgContainer} alt={r.document.id} />
                                    ) : (
                                        <div className={style.placeholder}>
                                            <div className={style.placeholderText}>Icon file is loading...</div>
                                        </div>
                                    )}
                                </div>
                                <div className={style.meta}>
                                    <div className={style.metaHeader}>
                                        <div className={style.title}>{r.document.label || r.document.id}</div>
                                        <div className={style.tags}>
                                            {(r.document.styles || []).map((s: string) => (
                                                <span key={`${r.document.id}-${s}`} className={style.tag}>{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={style.keywords}>{(r.document.keywords || []).join(", ")}</div>
                                    {llmChosen && (
                                        <div
                                            key="llm-reason"
                                            className={style.llmReason}
                                        >
                                            <span>GPT-4o preferred this icon: </span>
                                            <span style={{ fontStyle: "italic" }}>{llmChooseReason}</span>
                                        </div>
                                    )}
                                </div>
                                <div className={style.scores}>
                                    {rerankerScore && (
                                        <div
                                            className={style.primaryScore}
                                            style={{ color: calculateColor(rerankerScore, 0.0, 4.0) }}
                                            title={`Semantic Ranking: ${rerankerScore}`}
                                        >
                                            {rerankerScore?.toFixed(4)}
                                        </div>
                                    )}
                                    <div
                                        className={rerankerScore ? style.secondaryScore : style.primaryScore}
                                        style={{
                                            color: (() => {
                                                switch (searchModeStatus) {
                                                    case "vector": return calculateColor(score, 0.333, 1.0);
                                                    case "hybrid": return calculateColor(score, 0.0, 1 / 30);
                                                    default: return "";
                                                }
                                            })(),
                                        }}
                                        title={`${searchModeStatus?.charAt(0).toUpperCase()}${searchModeStatus?.slice(1)} Search: ${score}`}
                                    >
                                        {score?.toFixed(searchModeStatus === "hybrid" ? 6 : 4)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {(totalCount > 0 || isLoading) && (
                        <div className={style.pager}>
                            {lastSearchTimeMs !== null && (
                                <div className={style.pageInfo}>
                                    {`Last search duration: ${lastSearchTimeMs} ms`}
                                </div>
                            )}
                            {isLoading ? (
                                <Spinner label="Searching..." />
                            ) : results.length === 0 ? (
                                <div className={style.pageInfo}>No results</div>
                            ) : results.length < totalCount ? (
                                <PrimaryButton
                                    type="button"
                                    onClick={() => { const next = pageIndex + 1; setPageIndex(next); runSearch(next); }}
                                    aria-label="Load more results"
                                >
                                    Load more
                                </PrimaryButton>
                            ) : (
                                <div className={style.pageInfo}>All results loaded</div>
                            )}
                        </div>
                    )}
                </div>
            </Stack>
        </div>
    );
};

export default Icon;
